import { useState, useEffect } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ButtonVanilla, Typography, PlusIcon } from '@ag1/ui';
import { urlFor } from '@portal/shared/utils/urlFor';
import { vars } from '@ag1/ui/theme';
import { getSanityLinkUrl } from '@portal/shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import clsx from 'clsx';
import Image from 'next/image';
import { ImageAccordion as ImageAccordionProps, SanityKeyed } from 'types/studio/studio';
import { CommonSectionProps } from '../types';
import { styles } from './styles.css';

const { layoutSpacing } = vars;

const ImageAccordion = (props: SanityKeyed<ImageAccordionProps> & CommonSectionProps) => {
  const {
    _key: accordionKey,
    heading,
    caption,
    headingColorDesktop,
    headingColorMobile,
    imageCard = [],
    sectionWidth
  } = props;

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isHoverable, setIsHoverable] = useState(false);
  const imageCardCount: number = imageCard.length;

  // expand the first item by default
  // only run once on mount — imageCard is stable and we don't want re-renders to trigger this
  useEffect(() => {
    if (imageCard.length > 0 && expandedKey === null) {
      setExpandedKey(imageCard[0]._key);
    }

    const mq = window.matchMedia('(hover: hover)');
    setIsHoverable(mq.matches);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const locale = useLocale();

  const toggleAccordionItem = async (key: string) => {
    setExpandedKey((prevKey) => (prevKey === key ? null : key));
  };

  return (
    <div
      className={clsx(
        styles.accordionWrapperBase,
        styles.accordionWrapper[sectionWidth as keyof typeof layoutSpacing]
      )}
      key={accordionKey}>
      {/* Mobile heading */}
      {heading && (
        <div className={styles.accordionHeading.mobile}>
          <Typography
            variant="headlineSans"
            noPadding
            size="2"
            style={{
              color:
                vars.colors.text[headingColorMobile as keyof typeof vars.colors.text] ||
                vars.colors.text.primary
            }}>
            {heading}
          </Typography>
        </div>
      )}
      <div className={styles.accordionContainer}>
        {/* Desktop heading */}
        {heading && (
          <div className={styles.accordionHeading.desktop}>
            <Typography
              variant="headlineSans"
              noPadding
              size="2"
              style={{
                color:
                  vars.colors.text[headingColorDesktop as keyof typeof vars.colors.text] ||
                  vars.colors.text.primaryInverse
              }}>
              {heading}
            </Typography>
            {caption && (
              <Typography
                variant="caption"
                fontStyle="italic"
                noPadding
                size="1"
                style={{
                  color:
                    vars.colors.text[headingColorDesktop as keyof typeof vars.colors.text] ||
                    vars.colors.text.primaryInverse
                }}>
                {caption}
              </Typography>
            )}
          </div>
        )}
        {imageCard.map((card) => {
          const {
            _key,
            backgroundColor,
            backgroundImage,
            overlayImage,
            overlayGradient,
            title,
            subtitle,
            textColor,
            ctaText,
            ctaUrl,
            ctaStyle,
            ctaVariant,
            showIcon,
            iconPosition
          } = card;

          const ctaHref = getSanityLinkUrl(ctaUrl, locale);

          let icon = null;
          if (showIcon) {
            if (iconPosition === 'left') {
              icon = <ArrowLeftIcon size="20" />;
            } else if (iconPosition === 'right') {
              icon = <ArrowRightIcon size="20" />;
            }
          }

          const backgroundImageUrl = backgroundImage ? urlFor(backgroundImage).url : undefined;

          const overlayImageUrl = overlayImage ? urlFor(overlayImage).url : undefined;

          const toggleState = expandedKey === _key ? 'expanded' : 'collapsed';

          const imageCardWidthKey = `imageCards_${toggleState}__${imageCardCount}` as const;

          return (
            <div
              key={_key}
              className={clsx(
                styles.imageContainerBase,
                styles.imageContainer[toggleState],
                styles.imageContainerWidth[
                  imageCardWidthKey as keyof typeof styles.imageContainerWidth
                ]
              )}
              style={{
                background:
                  vars.colors.background[backgroundColor as keyof typeof vars.colors.background]
              }}>
              {overlayImageUrl ||
                (backgroundImageUrl && (
                  <Image
                    src={overlayImageUrl || backgroundImageUrl}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    style={{
                      objectFit: 'cover'
                    }}
                    quality={90}
                  />
                ))}

              <div className={styles.cardContentWrapperOverlay[toggleState]}>
                {title && toggleState === 'collapsed' && (
                  <Typography
                    variant="headlineSans"
                    noPadding
                    size="3"
                    style={{
                      color:
                        vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                        vars.colors.text.primaryInverse
                    }}>
                    {title}
                  </Typography>
                )}
              </div>

              {caption && toggleState === 'expanded' && (
                <Typography
                  variant="caption"
                  fontStyle="italic"
                  noPadding
                  size="1"
                  style={{
                    color:
                      vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                      vars.colors.text.primaryInverse
                  }}
                  className={styles.imageCardCaption}>
                  {caption}
                </Typography>
              )}
              <div className={styles.imageCardContentWrapper[toggleState]}>
                <div
                  role="button"
                  tabIndex={0}
                  className={clsx(
                    styles.imageCardContentBase,
                    styles.imageCardContent[toggleState]
                  )}
                  style={
                    toggleState === 'collapsed'
                      ? {
                          background:
                            vars.colors.flavors.ag1.background[
                              overlayGradient as keyof typeof vars.colors.flavors.ag1.background
                            ]
                        }
                      : {
                          background:
                            'linear-gradient(rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.08) 100%)'
                        }
                  }
                  onKeyDown={
                    !isHoverable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') toggleAccordionItem(_key);
                        }
                      : undefined
                  }
                  onClick={!isHoverable ? () => toggleAccordionItem(_key) : undefined}
                  onMouseOver={isHoverable ? () => toggleAccordionItem(_key) : undefined}
                  onFocus={isHoverable ? () => toggleAccordionItem(_key) : undefined}>
                  <div className={styles.imageCardContentTextWrapper[toggleState]}>
                    {title && (
                      <Typography
                        variant="headlineSans"
                        noPadding
                        size={toggleState === 'expanded' ? '2' : '3'}
                        style={{
                          color:
                            vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                            vars.colors.text.primaryInverse
                        }}>
                        {title}
                      </Typography>
                    )}
                    {subtitle && (
                      <Typography
                        variant="portalText"
                        className={styles.imageCardContentSubtitle[toggleState]}
                        noPadding
                        size="1"
                        style={{
                          color:
                            vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                            vars.colors.text.primaryInverse
                        }}>
                        {subtitle}
                      </Typography>
                    )}
                  </div>

                  {/* mobile CTA */}
                  {ctaText && toggleState === 'expanded' ? (
                    <ButtonVanilla
                      className={`${styles.ctaButtonMobile[toggleState]}`}
                      href={ctaHref}
                      variant={ctaVariant}
                      size="S"
                      shape={ctaStyle || 'round'}
                      iconPosition={iconPosition}
                      icon={icon}>
                      {ctaText}
                    </ButtonVanilla>
                  ) : (
                    <PlusIcon
                      strokeWidth={1.5}
                      size={32}
                      color={
                        vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                        vars.colors.text.primaryInverse
                      }
                      className={styles.cardContentIcon}
                    />
                  )}
                </div>

                {/* desktop CTA */}
                {ctaText && (
                  <ButtonVanilla
                    className={clsx(
                      `${styles.ctaButtonDesktopBase}`,
                      `${styles.ctaButtonDesktop[toggleState]}`
                    )}
                    href={ctaHref}
                    variant={ctaVariant}
                    size="S"
                    shape={ctaStyle || 'round'}
                    iconPosition={iconPosition}
                    icon={icon}>
                    {ctaText}
                  </ButtonVanilla>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImageAccordion;
